import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChartOptions, TooltipItem } from 'chart.js';
import { Line } from 'react-chartjs-2';
import CarIcon from 'assets/icons/car.svg?react';
import lineGraphImg from 'assets/lineGraph.png';
import AGSimpleSelect from 'components/AGSelect/AGSimpleSelect/AGSimpleSelect';
import EmptyWidget from 'components/EmptyWidget/EmptyWidget';
import LineChartSkeleton from 'components/Skeletons/LineChartSkeleton';
import RectangleSkeleton from 'components/Skeletons/RectangleSkeleton';
import WidgetErrorMessage from 'components/WidgetErrorMessage/WidgetErrorMessage';
import WidgetBox from 'components/widgets/WidgetBox';
import CalendarIcon from 'assets/icons/calendar.svg?react';
import BarChartIcon from 'assets/icons/barChart.svg?react';
import { trackingClient } from 'clients';
import { HTTPError } from 'clients/HTTPClient';
import { HistoricalOccupancy } from 'types/CarTracking';
import { getMockHistoricalOccupancy } from 'mocks/historicalOccupancy';
import {
    comparePeriodOptions,
    ComparePeriod,
    getAveragePeakWeekday,
    getAvgSinceTimestamp,
    getComparePeriodLegend,
    getCurrentOccupancyDelta,
    getDailyTrendChartData,
    hasRenderableData,
} from './dailyTrendHelpers';
import KpiCard from '../KpiCard/KpiCard';
import styles from './DailyTrendWidget.module.css';

type DailyTrendWidgetProps = {
    spotUuid: string;
    timezone: string;
    isDemoMode?: boolean;
};

const DEFAULT_COMPARE_PERIOD: ComparePeriod = '4w';

function DailyTrendLoadingSkeleton(): JSX.Element {
    return (
        <div className={styles.skeletonContainer} data-testid="daily-trend-skeleton">
            <div className={styles.skeletonSummaryRow}>
                <div className={styles.skeletonKpiRow}>
                    {['current', 'peak', 'average'].map((id) => (
                        <div key={id} className={styles.skeletonKpiCard}>
                            <RectangleSkeleton width="120px" height="12px" />
                            <div className={styles.skeletonKpiValueRow}>
                                <div className={styles.skeletonIcon}>
                                    <RectangleSkeleton />
                                </div>
                                <div className={styles.skeletonKpiText}>
                                    <RectangleSkeleton width="52px" height="20px" />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                <div className={styles.skeletonFilter}>
                    <RectangleSkeleton width="140px" height="36px" />
                </div>
            </div>
            <div className={styles.skeletonChart}>
                <LineChartSkeleton />
            </div>
        </div>
    );
}

function DailyTrendWidget({ spotUuid, timezone, isDemoMode = false }: DailyTrendWidgetProps): JSX.Element {
    const [comparePeriod, setComparePeriod] = useState<ComparePeriod>(DEFAULT_COMPARE_PERIOD);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [report, setReport] = useState<HistoricalOccupancy | null>(null);

    const fetchHistoricalOccupancy = useCallback(async () => {
        if (!spotUuid) {
            return;
        }

        setLoading(true);
        setErrorMessage('');
        try {
            const response = isDemoMode
                ? getMockHistoricalOccupancy(comparePeriod)
                : await trackingClient.getHistoricalOccupancy(spotUuid, getAvgSinceTimestamp(comparePeriod));
            setReport(response);
        } catch (error) {
            if (HTTPError.isHTTPError(error)) {
                setErrorMessage(error.message);
            } else {
                setErrorMessage(error?.toString() || 'Something went wrong');
            }
        } finally {
            setLoading(false);
        }
    }, [comparePeriod, isDemoMode, spotUuid]);

    useEffect(() => {
        fetchHistoricalOccupancy();
    }, [fetchHistoricalOccupancy]);

    const compareLegendLabel = useMemo(
        () => getComparePeriodLegend(comparePeriod, timezone),
        [comparePeriod, timezone]
    );
    const chartData = useMemo(() => {
        if (!report) return null;
        return getDailyTrendChartData(report, compareLegendLabel);
    }, [compareLegendLabel, report]);

    const averagePeakWeekday = useMemo(() => getAveragePeakWeekday(timezone), [timezone]);
    const currentDelta = useMemo(
        () =>
            report && chartData
                ? getCurrentOccupancyDelta(report.current_occupancy, chartData.currentPeriodPastAverage)
                : null,
        [chartData, report]
    );

    const options: ChartOptions<'line'> = useMemo(
        () => ({
            responsive: true,
            maintainAspectRatio: false,
            interaction: {
                mode: 'index',
                intersect: false,
            },
            scales: {
                x: {
                    ticks: {
                        maxRotation: 0,
                        autoSkip: true,
                        maxTicksLimit: 10,
                    },
                },
                y: {
                    min: 0,
                    max: chartData?.yAxisMax,
                    grid: { display: true, borderDash: [2] },
                    ticks: {
                        stepSize: chartData?.yAxisStep,
                    },
                },
            },
            plugins: {
                legend: {
                    display: false,
                },
                tooltip: {
                    callbacks: {
                        title(tooltipItems: TooltipItem<'line'>[]): string {
                            return tooltipItems[0].label;
                        },
                        label(tooltipItem: TooltipItem<'line'>): string {
                            return `${tooltipItem.dataset.label} - ${tooltipItem.formattedValue}`;
                        },
                    },
                },
            },
        }),
        [chartData?.yAxisMax, chartData?.yAxisStep]
    );

    const isLoading = loading || !report;
    const showEmptyState = report && !hasRenderableData(report);
    const showReport = report && chartData && !showEmptyState;

    const content = useMemo(() => {
        if (errorMessage) {
            return <WidgetErrorMessage message={errorMessage} onRetry={fetchHistoricalOccupancy} />;
        }

        if (isLoading) {
            return <DailyTrendLoadingSkeleton />;
        }

        if (showEmptyState) {
            return <EmptyWidget imgUrl={lineGraphImg} message="Data will appear here once it's available." />;
        }

        if (showReport) {
            return (
                <div className={styles.content}>
                    <div className={styles.kpiRow}>
                        <KpiCard
                            icon={<CarIcon />}
                            title="Current Occupancy"
                            value={report.current_occupancy}
                            trend={
                                currentDelta
                                    ? { direction: currentDelta.direction, text: currentDelta.text }
                                    : undefined
                            }
                        />
                        <KpiCard
                            icon={<BarChartIcon />}
                            title="Peak Today"
                            value={report.today_peak}
                            meta={chartData.peakTodayTimeLabel ? `at ${chartData.peakTodayTimeLabel}` : undefined}
                        />
                        <KpiCard
                            icon={<CalendarIcon />}
                            title="Average Peak"
                            value={report.average_past_peak}
                            meta={`on ${averagePeakWeekday}s`}
                        />
                    </div>
                    <div className={styles.chartContainer}>
                        <Line
                            data={{
                                labels: chartData.labels,
                                datasets: chartData.datasets,
                            }}
                            options={options}
                            className={styles.chart}
                        />
                    </div>
                    <div className={styles.legendRow}>
                        <div className={styles.legendItem}>
                            <span className={`${styles.legendSquare} ${styles.today}`} />
                            Today
                        </div>
                        <div className={styles.legendItem}>
                            <span className={`${styles.legendSquare} ${styles.compare}`} />
                            {compareLegendLabel}
                        </div>
                    </div>
                </div>
            );
        }

        return null;
    }, [
        averagePeakWeekday,
        chartData,
        compareLegendLabel,
        currentDelta,
        errorMessage,
        fetchHistoricalOccupancy,
        isLoading,
        options,
        report,
        showEmptyState,
        showReport,
    ]);

    return (
        <WidgetBox
            title="Daily Trend"
            rightElement={
                <div className={styles.filterRow}>
                    <span className={styles.filterLabel}>Compare to</span>
                    <AGSimpleSelect
                        options={comparePeriodOptions}
                        selected={comparePeriod}
                        onSelect={(selection) => setComparePeriod(selection as ComparePeriod)}
                        width="160px"
                    />
                </div>
            }
        >
            <div data-testid="daily-trend-widget">{content}</div>
        </WidgetBox>
    );
}

export default DailyTrendWidget;
